import React, { useState, useEffect, useCallback } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Modal,
  Button,
  ProgressBar,
  Badge,
  Card,
  ListGroup,
  Tab,
  Tabs,
  Spinner,
  Alert,
  Row,
  Col,
  Form,
  InputGroup,
} from "react-bootstrap";
import {
  FaTasks,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaArrowUp,
  FaArrowDown,
  FaSearch,
  FaEdit,
  FaInfoCircle,
  FaCheckCircle,
  FaPlayCircle,
  FaClock,
} from "react-icons/fa";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// API endpoint from the original code
const TASK_API_BASE = "http://localhost:5890";

// Helper functions (kept as they are, they are well-defined)
const getBadgeVariant = (priority) => {
  switch (priority) {
    case "High": return "danger";
    case "Medium": return "warning";
    case "Low": return "success";
    default: return "secondary";
  }
};

const getStatusVariant = (status) => {
  switch (status) {
    case "Almost Done": return "success";
    case "In Progress": return "primary";
    case "Not Started": return "secondary";
    default: return "info";
  }
};

const getBorderColor = (priority) => {
  switch (priority) {
    case "High": return "border-danger";
    case "Medium": return "border-warning";
    case "Low": return "border-success";
    default: return "border-secondary";
  }
};

const getPriorityIcon = (priority) => {
  switch (priority) {
    case "High": return <FaExclamationTriangle className="text-danger me-1" />;
    case "Medium": return <FaArrowUp className="text-warning me-1" />;
    case "Low": return <FaArrowDown className="text-success me-1" />;
    default: return null;
  }
};

const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

const Developeprtasktable = () => {
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [editingTask, setEditingTask] = useState({
    status: "",
    progress: 0,
    assigned: "", // Add assigned field for clarity in the edit modal
  });
  const [developerId, setDeveloperId] = useState(null);

  // Extract developer ID from token on component mount
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (token) {
      const payload = decodeToken(token);
      if (payload && payload.id) {
        setDeveloperId(payload.id);
      } else {
        setError("Developer ID not found in token. Please log in again.");
        setLoading(false);
      }
    } else {
      setError("No authentication token found. Please log in.");
      setLoading(false);
    }
  }, []);

  const fetchTasks = useCallback(async () => {
    if (!developerId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.get(`${TASK_API_BASE}/tasks/${developerId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const formattedTasks = response.data.map(task => ({
        ...task,
        task: task.task || "Untitled Task",
        assigned: task.assigned || "Unassigned",
        dueDate: task.dueDate || null,
        progress: Number(task.progress) || 0,
        description: task.description || "",
      }));
      setTasks(formattedTasks);
      setError(null);
    } catch (err) {
      setError(`Failed to fetch tasks: ${err.message}`);
      console.error("Error fetching tasks:", err);
    } finally {
      setLoading(false);
    }
  }, [developerId]);

  // Fetch tasks when developerId is set
  useEffect(() => {
    fetchTasks();
  }, [developerId, fetchTasks]);

  // Calculate task statistics
  const taskStats = {
    total: tasks.length,
    highPriority: tasks.filter(task => task.priority === "High").length,
    mediumPriority: tasks.filter(task => task.priority === "Medium").length,
    lowPriority: tasks.filter(task => task.priority === "Low").length,
    completed: tasks.filter(task => task.status === "Almost Done").length,
    inProgress: tasks.filter(task => task.status === "In Progress").length,
    notStarted: tasks.filter(task => task.status === "Not Started").length,
  };

  const handleShowTaskModal = (task) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const handleCloseTaskModal = () => {
    setSelectedTask(null);
    setShowTaskModal(false);
  };

  const handleShowEditModal = (task) => {
    setEditingTask({
      status: task.status,
      progress: task.progress,
      assigned: task.assigned,
    });
    setSelectedTask(task);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedTask(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingTask(prev => ({
      ...prev,
      [name]: name === "progress" ? parseInt(value) : value
    }));
  };

  const handleUpdateTask = async () => {
    if (!selectedTask || !selectedTask._id) {
      toast.error("Task ID is missing. Cannot update.");
      return;
    }
    
    setLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      
      const updateData = {
        status: editingTask.status,
        progress: editingTask.progress,
      };

      await axios.put(
        `${TASK_API_BASE}/edit-task/${selectedTask._id}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Update local state without refetching all tasks for better performance
      setTasks(prev =>
        prev.map(task =>
          task._id === selectedTask._id ? { ...task, ...updateData } : task
        )
      );
      
      // Update selectedTask state to reflect changes in the detail modal
      setSelectedTask(prev => prev ? { ...prev, ...updateData } : null);
      
      handleCloseEditModal();
      toast.success("Task updated successfully!");
    } catch (err) {
      setError(`Failed to update task: ${err.message}`);
      toast.error(`Failed to update task: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = tasks.filter(task => {
    const taskName = String(task.task || "").toLowerCase();
    const searchTermLower = searchTerm.toLowerCase();
    return taskName.includes(searchTermLower);
  });

  const getTasksForTab = () => {
    switch (activeTab) {
      case "notStarted":
        return filteredTasks.filter(task => task.status === "Not Started");
      case "inProgress":
        return filteredTasks.filter(task => task.status === "In Progress");
      case "almostDone":
        return filteredTasks.filter(task => task.status === "Almost Done");
      default:
        return filteredTasks;
    }
  };

  const tasksToDisplay = getTasksForTab();

  return (
    <div className="container bg-light p-4 rounded-3 mt-4 shadow-lg border border-success">
      <ToastContainer position="bottom-right" />
      
      {/* Header with Search */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
        <h3 className="mb-3 mb-md-0 fw-bold text-success d-flex align-items-center">
          <FaTasks className="me-2" />
          Developer Task Dashboard
        </h3>
        <InputGroup className="w-100 w-md-auto" style={{ maxWidth: "350px" }}>
          <InputGroup.Text id="search-icon" className="bg-white border-end-0">
            <FaSearch className="text-muted" />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search tasks by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border-start-0"
          />
        </InputGroup>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="shadow-sm">
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Row className="mb-4 g-3">
        <Col md={3} sm={6}>
          <Card className="border-0 shadow-sm h-100 bg-white">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase text-muted mb-2">Total Tasks</h6>
                  <h3 className="mb-0 fw-bold">{taskStats.total}</h3>
                </div>
                <div className="bg-success bg-opacity-10 p-3 rounded-circle">
                  <FaTasks className="text-success" size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="border-0 shadow-sm h-100 bg-white">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase text-muted mb-2">High Priority</h6>
                  <h3 className="mb-0 fw-bold text-danger">{taskStats.highPriority}</h3>
                </div>
                <div className="bg-danger bg-opacity-10 p-3 rounded-circle">
                  <FaExclamationTriangle className="text-danger" size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="border-0 shadow-sm h-100 bg-white">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase text-muted mb-2">In Progress</h6>
                  <h3 className="mb-0 fw-bold text-primary">{taskStats.inProgress}</h3>
                </div>
                <div className="bg-primary bg-opacity-10 p-3 rounded-circle">
                  <FaPlayCircle className="text-primary" size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="border-0 shadow-sm h-100 bg-white">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase text-muted mb-2">Completed</h6>
                  <h3 className="mb-0 fw-bold text-success">{taskStats.completed}</h3>
                </div>
                <div className="bg-success bg-opacity-10 p-3 rounded-circle">
                  <FaCheckCircle className="text-success" size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4 bg-white rounded-top"
        fill
      >
        <Tab eventKey="all" title={<span className="d-flex align-items-center justify-content-center"><FaTasks className="me-2" />All Tasks</span>} />
        <Tab eventKey="notStarted" title={<span className="d-flex align-items-center justify-content-center"><FaClock className="me-2" />Not Started</span>} />
        <Tab eventKey="inProgress" title={<span className="d-flex align-items-center justify-content-center"><FaPlayCircle className="me-2" />In Progress</span>} />
        <Tab eventKey="almostDone" title={<span className="d-flex align-items-center justify-content-center"><FaCheckCircle className="me-2" />Completed</span>} />
      </Tabs>

      {loading ? (
        <div className="text-center py-5 bg-white rounded shadow-sm">
          <Spinner animation="border" variant="success" />
          <p className="mt-2 text-muted">Loading tasks...</p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="table-responsive d-none d-md-block bg-white rounded shadow-sm">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-success">
                <tr>
                  <th className="py-3">Task</th>
                  <th className="py-3">Priority</th>
                  <th className="py-3">Assigned</th>
                  <th className="py-3">Due Date</th>
                  <th className="py-3">Status</th>
                  <th className="py-3">Progress</th>
                  <th className="py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasksToDisplay.length > 0 ? (
                  tasksToDisplay.map((task) => (
                    <tr
                      key={task._id}
                      className={`${getBorderColor(task.priority)} border-start-4`}
                    >
                      <td onClick={() => handleShowTaskModal(task)} style={{ cursor: "pointer" }}>
                        <div className="fw-semibold text-dark">{task.task}</div>
                        <small className="text-muted">
                          {(task.description).substring(0, 50)}
                          {(task.description).length > 50 ? "..." : ""}
                        </small>
                      </td>
                      <td>
                        <Badge pill bg={getBadgeVariant(task.priority)} className="d-flex align-items-center justify-content-center">
                          {getPriorityIcon(task.priority)}
                          {task.priority}
                        </Badge>
                      </td>
                      <td>
                        <div className="fw-medium text-dark">{task.assigned}</div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaCalendarAlt className="me-2 text-secondary" />
                          {task.dueDate
                            ? format(parseISO(task.dueDate), "dd-MMM-yyyy")
                            : "No due date"}
                        </div>
                      </td>
                      <td>
                        <Badge bg={getStatusVariant(task.status)}>{task.status}</Badge>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <ProgressBar
                            now={task.progress}
                            className="flex-grow-1 me-2"
                            variant={
                              task.progress >= 90 ? "success" : 
                              task.progress > 50 ? "info" : "warning"
                            }
                          />
                          <span className="fw-medium text-dark">{task.progress}%</span>
                        </div>
                      </td>
                      <td>
                        <Button 
                          variant="outline-success" 
                          size="sm" 
                          onClick={() => handleShowEditModal(task)}
                          className="d-flex align-items-center"
                        >
                          <FaEdit className="me-1" />
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">
                      No tasks found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="d-md-none">
            {tasksToDisplay.length > 0 ? (
              tasksToDisplay.map((task) => (
                <Card
                  key={task._id}
                  className={`mb-3 shadow-sm border-start-4 ${getBorderColor(task.priority)}`}
                >
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div onClick={() => handleShowTaskModal(task)} style={{ cursor: "pointer" }}>
                        <Card.Title className="mb-1 text-success">{task.task}</Card.Title>
                        <small className="text-muted">
                          {(task.description).substring(0, 60)}
                          {(task.description).length > 60 ? "..." : ""}
                        </small>
                      </div>
                      <Badge pill bg={getBadgeVariant(task.priority)}>{task.priority}</Badge>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Badge bg={getStatusVariant(task.status)} size="sm">
                        {task.status}
                      </Badge>
                      <div>
                        <FaCalendarAlt className="me-2 text-secondary" />
                        <small>Due: {task.dueDate ? format(parseISO(task.dueDate), "dd-MMM-yyyy") : "N/A"}</small>
                      </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="fw-medium text-muted">Progress:</small>
                      <small className="fw-medium text-dark">{task.progress}%</small>
                    </div>
                    <ProgressBar 
                      now={task.progress}
                      variant={
                        task.progress >= 90 ? "success" : 
                        task.progress > 50 ? "info" : "warning"
                      }
                      className="mb-3"
                    />
                    <Button 
                      variant="outline-success" 
                      size="sm" 
                      onClick={() => handleShowEditModal(task)}
                      className="w-100 d-flex align-items-center justify-content-center"
                    >
                      <FaEdit className="me-1" />
                      Edit Status & Progress
                    </Button>
                  </Card.Body>
                </Card>
              ))
            ) : (
              <Alert variant="info" className="text-center">No tasks found for this status.</Alert>
            )}
          </div>
        </>
      )}

      {/* Task Details Modal */}
      {selectedTask && (
        <Modal show={showTaskModal} onHide={handleCloseTaskModal} centered size="lg">
          <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-bold text-success d-flex align-items-center">
              <FaInfoCircle className="me-2" />
              Task Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-0">
            <h4 className="fw-bold mb-3">{selectedTask.task}</h4>
            <hr />
            <div className="d-flex flex-wrap gap-3 mb-4">
              <Badge pill bg={getBadgeVariant(selectedTask.priority)} className="d-flex align-items-center">
                {getPriorityIcon(selectedTask.priority)}
                {selectedTask.priority} Priority
              </Badge>
              <Badge bg={getStatusVariant(selectedTask.status)} className="d-flex align-items-center">
                <FaPlayCircle className="me-1" />
                {selectedTask.status}
              </Badge>
              <Badge bg="secondary" className="d-flex align-items-center">
                <FaCalendarAlt className="me-1" />
                Due: {selectedTask.dueDate ? format(parseISO(selectedTask.dueDate), "dd-MMM-yyyy") : "N/A"}
              </Badge>
            </div>

            <div className="mb-4">
              <h6 className="fw-bold text-muted">Description</h6>
              <Card className="bg-light border-0">
                <Card.Body>
                  <p className="text-dark mb-0">{selectedTask.description}</p>
                </Card.Body>
              </Card>
            </div>

            <Row className="mb-4">
              <Col md={6}>
                <h6 className="fw-bold text-muted">Assigned To</h6>
                <p className="fw-bold text-dark">{selectedTask.assigned}</p>
              </Col>
              <Col md={6}>
                <h6 className="fw-bold text-muted">Progress</h6>
                <ProgressBar
                  now={selectedTask.progress}
                  variant={
                    selectedTask.progress >= 90 ? "success" :
                    selectedTask.progress > 50 ? "info" : "warning"
                  }
                  label={`${selectedTask.progress}%`}
                  className="mt-2"
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseTaskModal}>
              Close
            </Button>
            <Button variant="success" onClick={() => handleShowEditModal(selectedTask)}>
              <FaEdit className="me-1" />
              Edit Task
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Edit Task Modal */}
      {selectedTask && (
        <Modal show={showEditModal} onHide={handleCloseEditModal} centered>
          <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-bold text-success d-flex align-items-center">
              <FaEdit className="me-2" />
              Update Task Progress
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h5 className="mb-4 text-dark">{selectedTask.task}</h5>
            
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold text-muted">Status</Form.Label>
              <Form.Select
                name="status"
                value={editingTask.status}
                onChange={handleEditChange}
              >
                <option value="Not Started">Not Started</option>
                <option value="In Progress">In Progress</option>
                <option value="Almost Done">Almost Done</option>
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-muted d-block">
                Progress: <Badge bg="success">{editingTask.progress}%</Badge>
              </Form.Label>
              <Form.Range
                name="progress"
                min="0"
                max="100"
                step="5"
                value={editingTask.progress}
                onChange={handleEditChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleUpdateTask} disabled={loading}>
              {loading ? (
                <Spinner as="span" size="sm" animation="border" role="status" className="me-2" />
              ) : (
                <FaEdit className="me-2" />
              )}
              {loading ? "Updating..." : "Update Task"}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default Developeprtasktable;